import {
  type JSX,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Radio } from "./Radio";
import { RadioGroup } from "./RadioGroup";
import {
  type RadioGroupDataProps,
  radioGroupDataSchema,
} from "./RadioGroup.schema";
import { RADIO_GROUP_ITEM_TAG_NAME, RADIO_GROUP_TAG_NAME } from "./constants";

export interface RadioGroupHandle {
  setValue: (value: string) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [RADIO_GROUP_TAG_NAME]: {
      props: RadioGroupDataProps;
      handle: RadioGroupHandle;
    };
  }
  interface InertTagNames {
    [RADIO_GROUP_ITEM_TAG_NAME]: true;
  }
}

const RWCRadioGroupItem = rwc(RADIO_GROUP_ITEM_TAG_NAME);

export function readRadioGroupSlotted(
  el: HTMLElement,
): Partial<RadioGroupDataProps> {
  const radios = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCRadioGroupItem.tagName}`),
  ).map((item) => ({
    value: item.getAttribute("value") ?? "",
    label: item.textContent?.trim() ?? "",
    description: item.getAttribute("description") ?? undefined,
  }));

  return {
    radios,
    onValueChange: (value) => {
      el.setAttribute("value", value);

      el.dispatchEvent(
        new CustomEvent("radio-group:change", {
          detail: { value },
          bubbles: true,
        }),
      );
    },
  };
}

function RWCRadioGroupComponent({
  ref,
  radios = [],
  defaultValue,
  value: controlledValue,
  onValueChange,
  ...props
}: Partial<RadioGroupDataProps> & {
  ref?: Ref<RadioGroupHandle>;
}): JSX.Element {
  const host = useIslandHost();

  const [value, setValue] = useState(controlledValue ?? defaultValue);

  useImperativeHandle(
    ref,
    () => ({
      setValue: (newValue) => {
        setValue(newValue);
        onValueChange?.(newValue);
      },
    }),
    [onValueChange],
  );

  useLayoutEffect(() => {
    if (defaultValue) {
      onValueChange?.(defaultValue);
    }
  }, []);

  useLayoutEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
      onValueChange?.(controlledValue);
    }
  }, [controlledValue]);

  useEffect(() => {
    getElementInternals(host)?.setFormValue(value ?? null);
  }, [host, value]);

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onValueChange?.(newValue);
  };

  return (
    <RadioGroup value={value} onValueChange={handleValueChange} {...props}>
      {radios.map((radioItem) => (
        <Radio
          key={radioItem.value}
          value={radioItem.value}
          label={radioItem.label}
          description={radioItem.description}
        />
      ))}
    </RadioGroup>
  );
}

const RWCRadioGroup = rwc(RADIO_GROUP_TAG_NAME, {
  component: RWCRadioGroupComponent,
  dataSchema: radioGroupDataSchema,
  usesSlots: true,
  readSlotted: readRadioGroupSlotted,
  formAssociated: true,
});

componentManager.register(RWCRadioGroupItem, RWCRadioGroup);
